import React, { useState, useEffect, useCallback } from 'react';
import { MdOutlineChevronLeft, MdOutlineChevronRight } from 'react-icons/md';
import IncrementButton from './components/IncrementButton';

const SCROLL_STEP_AMOUNT = 300;

type Props = {
  scrollRef: HTMLDivElement;
  scrollMenuTo: (value: number) => void;
};

export default function ScrollIncrementsButtons({
  scrollRef,
  scrollMenuTo
}: Props) {
  const [isScrollAtStart, setIsScrollAtStart] = useState(true);
  const [isScrollAtEnd, setIsScrollAtEnd] = useState(false);

  // Check if scroll area has overflow and update scroll position
  const checkMenuScrollPosition = useCallback(() => {
    const hasOverflow = scrollRef.scrollWidth > scrollRef.clientWidth;

    // If there's no overflow, we're both at start and end
    if (!hasOverflow) {
      setIsScrollAtStart(true);
      setIsScrollAtEnd(true);
      return;
    }

    // Check if scrolled to the end
    const isAtEnd =
      scrollRef.scrollWidth <=
      Math.ceil(scrollRef.scrollLeft + scrollRef.clientWidth);

    setIsScrollAtStart(scrollRef.scrollLeft <= 0);
    setIsScrollAtEnd(isAtEnd);
  }, []);

  const handlePrevClick = () => {
    if (!isScrollAtStart) {
      scrollMenuTo(scrollRef.scrollLeft - SCROLL_STEP_AMOUNT);
    }
  };

  const handleNextClick = () => {
    if (!isScrollAtEnd) {
      scrollMenuTo(scrollRef.scrollLeft + SCROLL_STEP_AMOUNT);
    }
  };

  // Add scroll event listener to horizontal scroll menu to check for overflow
  // Add resize event listener to horizontal scroll menu to check for overflow
  useEffect(() => {
    let isScrollingOrResizing: NodeJS.Timeout;

    const debouncedCheckMenuScrollPosition = () => {
      clearTimeout(isScrollingOrResizing);
      isScrollingOrResizing = setTimeout(() => {
        checkMenuScrollPosition();
      }, 100);
    };

    checkMenuScrollPosition();

    scrollRef.addEventListener('scroll', debouncedCheckMenuScrollPosition);
    window.addEventListener('resize', debouncedCheckMenuScrollPosition);

    return () => {
      clearTimeout(isScrollingOrResizing);
      scrollRef.removeEventListener('scroll', debouncedCheckMenuScrollPosition);
      window.removeEventListener('resize', debouncedCheckMenuScrollPosition);
    };
  }, []);

  return (
    <div className="hidden md:flex gap-2 items-center pl-2">
      <IncrementButton
        onClick={handlePrevClick}
        disabled={isScrollAtStart}
        ariaLabel="Previous"
      >
        <MdOutlineChevronLeft size={24} />
      </IncrementButton>
      <IncrementButton
        onClick={handleNextClick}
        disabled={isScrollAtEnd}
        ariaLabel="Next"
      >
        <MdOutlineChevronRight size={24} />
      </IncrementButton>
    </div>
  );
}
